import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Server,
  FileText,
  Eye,
  Building2,
  Network,
  Users,
  Scale,
  Bot,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import LandingDarkScope from "@/components/LandingDarkScope";
import MarketingSiteChrome from "@/components/MarketingSiteChrome";
import { API_BASE } from "@/lib/utils";
import {
  TRUST_LAST_UPDATED,
  TRUST_HERO,
  COMPLIANCE_ITEMS,
  PRODUCT_SCOPE,
  CONTROL_SECTIONS,
  REPORTS,
  FAQS,
  TOC,
} from "@/content/trust-centre";

const SECTION_ICONS = {
  "product-security": ShieldCheck,
  "data-security": Lock,
  "app-security": FileText,
  "access-control": Eye,
  infrastructure: Server,
  "network-security": Network,
  "corporate-security": Users,
  "data-privacy": Scale,
  legal: Building2,
  "ai-governance": Bot,
};

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("progress")) return "text-amber-300 border-amber-300/30 bg-amber-300/10";
  if (s.includes("live") || s.includes("aligned")) return "text-emerald-300 border-emerald-300/30 bg-emerald-300/10";
  return "text-[var(--text-secondary)] border-[var(--border-default)] bg-[var(--bg-surface)]";
}

export default function TrustPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    documents: ["soc2-report"],
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const selectedLabels = useMemo(() => {
    const map = Object.fromEntries(REPORTS.map((r) => [r.id, r.title]));
    return form.documents.map((id) => map[id] || id);
  }, [form.documents]);

  const toggleDoc = (id) => {
    setForm((prev) => {
      const has = prev.documents.includes(id);
      return {
        ...prev,
        documents: has ? prev.documents.filter((d) => d !== id) : [...prev.documents, id],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim()) {
      setError("Work email is required.");
      return;
    }
    if (!form.documents.length) {
      setError("Select at least one document or questionnaire.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/public/inbound`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          intent: "security",
          email: form.email.trim(),
          name: form.name.trim(),
          company: form.company.trim(),
          message: [
            "Trust Centre document request",
            `Documents: ${selectedLabels.join("; ")}`,
            form.message.trim(),
          ]
            .filter(Boolean)
            .join("\n"),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Request failed (${res.status})`);
      setDone(true);
    } catch (err) {
      setError(err?.message || "Could not submit your request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <LandingDarkScope>
      <MarketingSiteChrome contentClassName="max-w-[1100px] mx-auto px-4 md:px-8">
        <div className="pt-4 pb-16">
          <div className="flex flex-wrap items-center gap-3 text-overline text-[var(--primary)]">
            <ShieldCheck size={14} />
            {TRUST_HERO.overline}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mt-3 max-w-3xl">
            {TRUST_HERO.title}
          </h1>
          <p className="text-base md:text-lg text-[var(--text-secondary)] mt-5 leading-relaxed max-w-3xl">
            {TRUST_HERO.lede}
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-4">
            Last updated: {TRUST_LAST_UPDATED}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#request" className="btn-primary inline-flex items-center gap-2">
              Request security docs <ArrowRight size={16} />
            </a>
            <Link to="/status" className="btn-secondary inline-flex items-center gap-2">
              Platform status
            </Link>
            <a href="mailto:security@aivoryems.com" className="btn-secondary inline-flex items-center gap-2">
              security@aivoryems.com
            </a>
          </div>

          <nav
            className="mt-10 flex flex-wrap gap-2 text-xs"
            aria-label="Trust Centre sections"
          >
            {TOC.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="px-3 py-1.5 rounded-md border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)]/40 transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <section id="compliance" className="mt-16 scroll-mt-28">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Compliance</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
              Same assurance families enterprise security teams expect from Cvent and Gevme —
              with honest status labels while the SOC 2 Type II attestation completes.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {COMPLIANCE_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)]/60 p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-lg">{item.name}</h3>
                    <span
                      className={`shrink-0 text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded-full border ${statusTone(item.status)}`}
                    >
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">
                    {item.detail}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section id="scope" className="mt-16 scroll-mt-28">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">{PRODUCT_SCOPE.title}</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2">{PRODUCT_SCOPE.body}</p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {PRODUCT_SCOPE.products.map((p) => (
                <div
                  key={p.name}
                  className="rounded-xl border border-[var(--border-default)] p-5"
                >
                  <h3 className="font-heading text-lg">{p.name}</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-[var(--text-secondary)]">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-2">
                        <CheckCircle2 size={14} className="text-[var(--primary)] mt-0.5 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {CONTROL_SECTIONS.map((section) => {
            const Icon = SECTION_ICONS[section.id] || ShieldCheck;
            return (
              <section key={section.id} id={section.id} className="mt-16 scroll-mt-28">
                <div className="flex items-center gap-2 text-[var(--primary)]">
                  <Icon size={18} />
                  <h2 className="font-heading text-2xl md:text-3xl tracking-tight text-[var(--text-primary)]">
                    {section.title}
                  </h2>
                </div>
                <div className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-6">
                  {section.items.map((item) => (
                    <div key={item.title}>
                      <h3 className="text-sm font-semibold text-[var(--text-primary)]">{item.title}</h3>
                      <p className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed">
                        {item.body}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          <section id="reports" className="mt-16 scroll-mt-28">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Reports & questionnaires</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
              Security packs for customer due diligence — request under NDA from the form below
              (same workflow as peer trust portals).
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {REPORTS.map((r) => (
                <div
                  key={r.id}
                  className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)]/40 p-5"
                >
                  <h3 className="font-heading text-lg">{r.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{r.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="faq" className="mt-16 scroll-mt-28">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Security FAQ</h2>
            <div className="mt-8 space-y-5">
              {FAQS.map((item) => (
                <div key={item.q} className="border-b border-[var(--border-default)] pb-5">
                  <h3 className="text-sm font-semibold">{item.q}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="request" className="mt-16 scroll-mt-28">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight">Request security documentation</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
              Tell us which artefacts your security team needs. We respond from{" "}
              <a className="text-[var(--primary)] hover:underline" href="mailto:security@aivoryems.com">
                security@aivoryems.com
              </a>
              .
            </p>

            {done ? (
              <div className="mt-8 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-6 max-w-xl">
                <div className="flex items-center gap-2 text-emerald-300">
                  <CheckCircle2 size={18} />
                  <span className="font-medium">Request received</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">
                  Thanks — our security team will follow up at <strong className="text-[var(--text-primary)]">{form.email}</strong>{" "}
                  with next steps (NDA if required, then document access).
                </p>
                <button
                  type="button"
                  className="btn-secondary mt-6"
                  onClick={() => {
                    setDone(false);
                    setForm((p) => ({ ...p, message: "" }));
                  }}
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 max-w-xl space-y-4" data-testid="trust-doc-request-form">
                <div>
                  <label className="text-overline">Work email</label>
                  <input
                    type="email"
                    required
                    className="input-dark mt-2 w-full"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-overline">Name</label>
                    <input
                      type="text"
                      className="input-dark mt-2 w-full"
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label className="text-overline">Company</label>
                    <input
                      type="text"
                      className="input-dark mt-2 w-full"
                      value={form.company}
                      onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
                      placeholder="Organisation"
                      autoComplete="organization"
                    />
                  </div>
                </div>
                <fieldset>
                  <legend className="text-overline">Documents requested</legend>
                  <div className="mt-3 space-y-2">
                    {REPORTS.map((r) => (
                      <label
                        key={r.id}
                        className="flex items-start gap-3 text-sm text-[var(--text-secondary)] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={form.documents.includes(r.id)}
                          onChange={() => toggleDoc(r.id)}
                        />
                        <span>
                          <span className="text-[var(--text-primary)] font-medium">{r.title}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div>
                  <label className="text-overline">Notes (optional)</label>
                  <textarea
                    className="input-dark mt-2 w-full min-h-[100px]"
                    value={form.message}
                    onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                    placeholder="RFP deadline, questionnaire format, NDA contact…"
                  />
                </div>
                {error ? <p className="text-sm text-red-400">{error}</p> : null}
                <button type="submit" className="btn-primary" disabled={busy} data-testid="trust-doc-submit">
                  {busy ? "Sending…" : "Request access"}
                </button>
              </form>
            )}
          </section>

          <div className="mt-16 pt-8 border-t border-[var(--border-default)] flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="text-[var(--primary)] hover:underline">
              Privacy policy
            </Link>
            <Link to="/terms" className="text-[var(--primary)] hover:underline">
              Terms of service
            </Link>
            <Link to="/status" className="text-[var(--primary)] hover:underline">
              Status
            </Link>
            <a href="/.well-known/security.txt" className="text-[var(--primary)] hover:underline">
              security.txt
            </a>
            <a href="mailto:security@aivoryems.com" className="text-[var(--primary)] hover:underline">
              security@aivoryems.com
            </a>
          </div>
        </div>
      </MarketingSiteChrome>
    </LandingDarkScope>
  );
}
